import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { testWeeklyDealsEmail } from "src/api/admin/maria";
import { useToastStore } from "src/store/toast";
import { Loader2, Mail, Settings } from "lucide-react";

interface WeeklyDealsEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WeeklyDealsEmailDialog: React.FC<WeeklyDealsEmailDialogProps> = ({ open, onOpenChange }) => {
  const { addToastSuccess, addToastDanger } = useToastStore();
  const [formData, setFormData] = React.useState({
    max_properties_per_email: 4,
    days_back: 7,
    location_filter: "near_user",
    recipient_email: "",
  });

  const testEmailMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await testWeeklyDealsEmail(data);
      console.log(response);
      return response;
    },
    onSuccess: (data) => {
      addToastSuccess({
        title: "Success",
        body: "Weekly deals email test completed successfully",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      addToastDanger({
        title: "Error",
        body: error?.response?.data?.error || "Failed to test weekly deals email",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    testEmailMutation.mutate(formData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 px-3">
            <Mail className="w-5 h-5" />
            Test Weekly Deals Email
          </DialogTitle>
          <DialogDescription>Test the weekly deals email campaign functionality.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="max_properties">Max Properties per Email</Label>
              <Input
                id="max_properties"
                type="number"
                min="1"
                max="10"
                value={formData.max_properties_per_email}
                onChange={(e) => handleInputChange("max_properties_per_email", parseInt(e.target.value, 10) || 4)}
                placeholder="4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="days_back">Days Back</Label>
              <Input
                id="days_back"
                type="number"
                min="1"
                max="30"
                value={formData.days_back}
                onChange={(e) => handleInputChange("days_back", parseInt(e.target.value, 10) || 7)}
                placeholder="7"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location_filter">Location Filter</Label>
            <Select value={formData.location_filter} onValueChange={(value) => handleInputChange("location_filter", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select location filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="near_user">Near User</SelectItem>
                <SelectItem value="all_locations">All Locations</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipient_email">Recipient Email (Optional)</Label>
            <Input
              id="recipient_email"
              type="email"
              value={formData.recipient_email}
              onChange={(e) => handleInputChange("recipient_email", e.target.value)}
              placeholder="test@example.com"
            />
            <p className="text-xs text-muted-foreground">Leave empty to test with all recipients, or specify a single email for testing</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={testEmailMutation.isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={testEmailMutation.isLoading} className="bg-primary hover:bg-primary/90">
              {testEmailMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Test Email Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyDealsEmailDialog;
